/**
 * Package Card Component
 * Displays a subscription package with features
 */

import { cn } from '@/renderer/lib/utils';
import {
  getAnnualDiscountPercentage,
  getBillingCycleDescriptor,
  getPackageAnnualSavingsAmount,
  getPackageMonthlyEquivalent,
  getPackageTotalPriceForCycle,
} from '@/renderer/services/billingPricing';
import { BillingCycle, SubscriptionPackage } from '@/renderer/types/billing';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import React from 'react';

import { BillingStatusBadge } from './BillingStatusBadge';
import { BillingSurface } from './BillingSurface';
import { formatMoney } from './formatters';

interface PackageCardProps {
  actionDisabled?: boolean;
  actionHint?: string;
  actionLabel?: string;
  annualDiscountPercentage?: number;
  annualPrice?: number;
  billingCycle?: BillingCycle;
  description?: string;
  featured?: boolean;
  features: string[];
  isCurrent: boolean;
  name: string;
  onAction?: () => void;
  packageId: SubscriptionPackage;
  price: number;
}

export const PackageCard: React.FC<PackageCardProps> = ({
  actionDisabled = false,
  actionHint,
  actionLabel = 'View plan',
  annualDiscountPercentage = 0,
  annualPrice,
  billingCycle = 'monthly',
  description,
  featured = false,
  features,
  isCurrent,
  name,
  onAction,
  packageId,
  price,
}) => {
  const tone = isCurrent ? 'inverse' : featured ? 'tinted' : 'default';
  const resolvedAnnualDiscount = getAnnualDiscountPercentage({
    id: packageId,
    annual_discount_percentage: annualDiscountPercentage,
  });
  const monthlyEquivalent = getPackageMonthlyEquivalent(
    {
      annual_discount_percentage: resolvedAnnualDiscount,
      annual_price: annualPrice || 0,
      id: packageId,
      price,
    },
    billingCycle,
  );
  const totalPrice = getPackageTotalPriceForCycle(
    {
      annual_discount_percentage: resolvedAnnualDiscount,
      annual_price: annualPrice || 0,
      id: packageId,
      price,
    },
    billingCycle,
  );
  const annualSavingsAmount = getPackageAnnualSavingsAmount({
    annual_discount_percentage: resolvedAnnualDiscount,
    annual_price: annualPrice || 0,
    id: packageId,
    price,
  });

  return (
    <BillingSurface
      tone={tone}
      className={cn(
        'flex h-full flex-col',
        isCurrent
          ? 'border-emerald-400/20'
          : 'transition hover:-translate-y-0.5',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className={cn(
              'text-[11px] font-medium uppercase tracking-[0.2em]',
              isCurrent ? 'text-emerald-200/80' : 'text-slate-500',
            )}
          >
            {featured && !isCurrent
              ? 'Scale with confidence'
              : 'Subscription plan'}
          </p>
          <h3
            className={cn(
              'mt-2 text-xl font-semibold',
              isCurrent ? 'text-white' : 'text-slate-900',
            )}
          >
            {name}
          </h3>
        </div>

        <BillingStatusBadge
          label={isCurrent ? 'Current' : featured ? 'Recommended' : 'Available'}
          tone={isCurrent ? 'inverse' : featured ? 'success' : 'neutral'}
        />
      </div>

      <div className="mt-4">
        {billingCycle === 'annual' && (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-base font-medium line-through',
                isCurrent ? 'text-slate-400' : 'text-slate-400',
              )}
            >
              {formatMoney(
                packageId === 'pro_semi_annual' ? price * 2 : price * 12,
              )}
              /year
            </span>
            <BillingStatusBadge
              label={`Save ${resolvedAnnualDiscount}%`}
              tone="success"
            />
          </div>
        )}

        <div className="mt-2 flex items-end gap-2">
          <span
            className={cn(
              'text-3xl font-semibold tracking-tight',
              isCurrent ? 'text-white' : 'text-slate-950',
            )}
          >
            {formatMoney(totalPrice)}
          </span>
          <span
            className={cn(
              'pb-1 text-sm font-medium',
              isCurrent ? 'text-slate-300' : 'text-slate-500',
            )}
          >
            {billingCycle === 'annual'
              ? '/year'
              : billingCycle === 'semi_anually'
                ? '/6 mo'
                : '/month'}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p
            className={cn(
              'text-xs font-medium uppercase tracking-[0.14em]',
              isCurrent ? 'text-emerald-200/90' : 'text-emerald-700',
            )}
          >
            {getBillingCycleDescriptor(billingCycle)}
          </p>
          {billingCycle !== 'monthly' && (
            <p
              className={cn(
                'text-xs',
                isCurrent ? 'text-slate-300' : 'text-slate-500',
              )}
            >
              Equivalent to {formatMoney(monthlyEquivalent)}/month
            </p>
          )}
        </div>
        {billingCycle === 'annual' && annualSavingsAmount > 0 && (
          <p
            className={cn(
              'mt-2 text-xs',
              isCurrent ? 'text-emerald-100' : 'text-emerald-700',
            )}
          >
            Save {formatMoney(annualSavingsAmount)} each year with annual
            billing.
          </p>
        )}
        <p
          className={cn(
            'mt-3 text-sm leading-6',
            isCurrent ? 'text-slate-300' : 'text-slate-500',
          )}
        >
          {description ||
            'Built for compact billing workflows and fast upgrades.'}
        </p>
      </div>

      <button
        type="button"
        onClick={onAction}
        disabled={actionDisabled || !onAction}
        className={cn(
          'mt-5 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60',
          isCurrent
            ? 'bg-white text-slate-900 hover:bg-emerald-50'
            : 'bg-slate-900 text-white hover:bg-slate-800',
        )}
      >
        {actionLabel}
        {!actionDisabled && onAction && <ArrowRight size={15} />}
      </button>

      {actionHint && (
        <p
          className={cn(
            'mt-3 text-xs leading-5',
            isCurrent ? 'text-slate-300' : 'text-rose-600',
          )}
        >
          {actionHint}
        </p>
      )}

      <ul className="mt-5 space-y-3">
        {features.map((feature) => (
          <li
            key={feature}
            className={cn(
              'flex items-start gap-2 text-sm',
              isCurrent ? 'text-slate-200' : 'text-slate-600',
            )}
          >
            <CheckCircle2
              size={15}
              className={cn(
                'mt-0.5 shrink-0',
                isCurrent ? 'text-emerald-300' : 'text-emerald-600',
              )}
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </BillingSurface>
  );
};
